import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@chakra-ui/icons'
import {
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function WeekInput({
  value,
  onChange,
  name,
  onBlur,
  ref,
  disabled,
  min,
  max
}: {
  value?: string
  onChange?: (value: string) => void
  name?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  ref?: React.RefCallback<HTMLInputElement>
  disabled?: boolean
  min?: string
  max?: string
}) {
  const { t } = useTranslation()

  const currentValue = moment(value).format(moment.HTML5_FMT.DATE)
  const minimalDate = min ? moment(min) : undefined
  const maximumDate = max ? moment(max) : undefined

  const {
    isOpen: isInputOpen,
    onClose: onInputClose,
    onOpen: onInputOpen
  } = useDisclosure()

  const [month, setMonth] = useState(
    moment(currentValue).format(moment.HTML5_FMT.DATE)
  )

  const weekOptions = useMemo(() => {
    const lastDayDate = moment(month).toDate()
    lastDayDate.setUTCDate(1)
    lastDayDate.setUTCMonth(lastDayDate.getUTCMonth() + 1)
    lastDayDate.setUTCDate(lastDayDate.getUTCDate() - 1)

    const lastDay = lastDayDate.getUTCDate()

    const weeks = new Set<number>()
    for (let i = 1; i < lastDay; i += 7) {
      lastDayDate.setUTCDate(i)
      weeks.add(moment(lastDayDate).utc().get('week'))
    }

    lastDayDate.setUTCDate(lastDay)
    weeks.add(moment(lastDayDate).utc().get('week'))

    return Array.from(weeks).map((week, i) => {
      const targetDate = moment(
        `${moment(month).get('year')}W${week.toString().padStart(2, '0')}`
      )
        .startOf('week')
        .format(moment.HTML5_FMT.DATE)

      return (
        <Button
          key={week}
          colorScheme="blue"
          variant={'ghost'}
          isDisabled={
            targetDate === currentValue ||
            (minimalDate &&
              minimalDate
                .clone()
                .startOf('week')
                .isAfter(moment(targetDate))) ||
            (maximumDate &&
              maximumDate.clone().startOf('week').isBefore(moment(targetDate)))
          }
          onClick={() => {
            onChange?.(targetDate)
          }}
        >
          <Text _firstLetter={{ textTransform: 'uppercase' }} mr="1">
            {t('general.common.timeUnits.week')}
          </Text>
          <Text>{i + 1}</Text>
        </Button>
      )
    })
  }, [month, currentValue])

  useEffect(() => {
    if (isInputOpen) return
    setMonth(moment(currentValue).format(moment.HTML5_FMT.DATE))
  }, [isInputOpen])

  return (
    <Popover isOpen={isInputOpen} onClose={onInputClose} placement="bottom-end">
      <PopoverTrigger>
        <InputGroup>
          <Input
            name={name}
            onBlur={onBlur}
            ref={ref}
            disabled={disabled}
            readOnly
            type="text"
            value={`${moment(currentValue)
              .startOf('week')
              .format('L')} - ${moment(currentValue)
              .endOf('week')
              .format('L')}`}
            borderColor={currentValue ? 'custom.gray.400' : 'custom.gray.600'}
          />
          <InputRightElement>
            <IconButton
              onClick={onInputOpen}
              backgroundColor={'transparent'}
              _hover={{ backgroundColor: 'transparent' }}
              icon={<CalendarIcon />}
              aria-label={t(
                'operational.liquids.visualization.aria.openWeekSelector'
              )}
            />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody>
          <Flex flexDir={'column'}>
            <Flex
              justifyContent={'space-between'}
              alignItems={'center'}
              w="full"
            >
              <IconButton
                backgroundColor={'transparent'}
                icon={<ChevronLeftIcon />}
                aria-label={t(
                  'operational.liquids.visualization.aria.previousMonth'
                )}
                isDisabled={
                  minimalDate &&
                  minimalDate.clone().isAfter(moment(month).startOf('month'))
                }
                onClick={() => {
                  setMonth(
                    moment(month)
                      .subtract(1, 'month')
                      .format(moment.HTML5_FMT.MONTH)
                  )
                }}
              />
              <Text _firstLetter={{ textTransform: 'uppercase' }}>
                {moment(month).format('MMMM yyyy')}
              </Text>
              <IconButton
                backgroundColor={'transparent'}
                icon={<ChevronRightIcon />}
                aria-label={t(
                  'operational.liquids.visualization.aria.nextMonth'
                )}
                isDisabled={
                  maximumDate &&
                  maximumDate.clone().isBefore(moment(month).endOf('month'))
                }
                onClick={() => {
                  setMonth(
                    moment(month).add(1, 'month').format(moment.HTML5_FMT.MONTH)
                  )
                }}
              />
            </Flex>
            <Flex
              gap="4"
              flexWrap={'wrap'}
              justifyContent={'space-around'}
              alignItems={'center'}
            >
              {weekOptions}
            </Flex>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
